/**
 * Lightweight JWT payload decoder — no crypto polyfills required.
 *
 * Security posture
 * ----------------
 * This helper intentionally does NOT verify the signature; that responsibility
 * belongs to the backend (SSO / /auth/check-auth endpoint).  What it DOES
 * enforce on the client side:
 *
 *  1. Token size cap          — rejects tokens larger than MAX_JWT_BYTES to
 *                               prevent oversized-payload attacks.
 *  2. Strict 3-part structure — header.payload.signature, nothing else.
 *  3. Header sanity check     — `alg` must be a non-empty string,
 *                               `typ` must be "JWT" when present.
 *  4. Standard time claims    — honours `exp` (expiry) and `nbf` (not-before)
 *                               when present so stale tokens are rejected early,
 *                               before any UI logic runs.
 *  5. Payload shape guard     — the decoded value must be a plain object.
 *
 * The function is intentionally synchronous and dependency-free.
 */

/** Maximum accepted raw token length (bytes).  RS256/HS256 tokens are
 *  typically 300-600 chars; 8 kB is a generous but safe ceiling. */
const MAX_JWT_BYTES = 8_192;

/** Allowed `typ` header values (case-insensitive). */
const ALLOWED_TYP = new Set(['jwt', 'at+jwt']);

/** Decode a base64url-encoded segment to a plain JS object. */
const decodeSegment = (segment: string): unknown => {
	// base64url → base64 (RFC 4648 §5)
	const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
	// Percent-encode each byte so decodeURIComponent handles multi-byte UTF-8
	const json = decodeURIComponent(
		atob(base64)
			.split('')
			.map((c) => '%' + ('00' + c.codePointAt(0)?.toString(16)).slice(-2))
			.join('')
	);
	return JSON.parse(json);
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
	typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Decode and lightly validate a JWT, returning its payload.
 *
 * Returns `null` on any structural, size, or temporal violation so callers
 * never have to handle thrown errors.
 *
 * @param token  Raw JWT string (e.g. from a cookie).
 * @returns      Typed payload or `null` if the token is invalid / expired.
 */
export const decodeJwtPayload = <T = unknown>(token: string): T | null => {
	try {
		// 1. Size cap
		if (!token || token.length > MAX_JWT_BYTES) return null;

		// 2. Exactly three dot-separated parts
		const parts = token.split('.');
		if (parts.length !== 3) return null;

		const [rawHeader, rawPayload] = parts;

		// 3. Header validation
		const header = decodeSegment(rawHeader);
		if (!isPlainObject(header)) return null;
		if (typeof header['alg'] !== 'string' || header['alg'] === '') return null;
		if ('typ' in header) {
			const typ = header['typ'];
			if (typeof typ !== 'string' || !ALLOWED_TYP.has(typ.toLowerCase()))
				return null;
		}

		// 4. Payload decoding
		const payload = decodeSegment(rawPayload);
		if (!isPlainObject(payload)) return null;

		// 5. Standard time claims (seconds since epoch)
		const now = Math.floor(Date.now() / 1_000);

		if ('exp' in payload) {
			const exp = payload['exp'];
			if (typeof exp !== 'number' || now >= exp) return null;
		}

		if ('nbf' in payload) {
			const nbf = payload['nbf'];
			if (typeof nbf !== 'number' || now < nbf) return null;
		}

		return payload as T;
	} catch {
		return null;
	}
};
